import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GeneratedMessage } from '@trinity/chat/types/message-generation';
import { Body } from '@trinity/chat-components/components/Modal/Body.js';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet.js';
import { Header } from '@trinity/chat-components/components/Modal/Header.js';
import { Modal } from '@trinity/chat-components/components/Modal/Modal.js';
import { ButtonActivityIndicator } from '@trinity/chat-components/components/presentation/Button.ActivityIndicator.js';
import { ButtonOutlined } from '@trinity/chat-components/components/presentation/ButtonOutlined.js';
import { Textarea } from '@trinity/chat-components/components/presentation/Textarea.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { Input } from '@/components/presentation/Input';
import { Select } from '@/components/presentation/Select';

interface MessageEditorModalProps {
  open: boolean;
  onClose: () => void;
  message: GeneratedMessage | null;
  messageGenerationSessionId?: number;
  brands: string[];
  onSave?: (message: GeneratedMessage) => void;
  enableSegmentSelection?: boolean;
  preSelectedSegmentationRunId?: number | null;
  preSelectedSegmentId?: string | null;
}

const STATUSES: Array<'draft' | 'approved' | 'published'> = ['draft', 'approved', 'published'];

const DEFAULT_BRANDS = [
  'Elderberry',
  'Banana',
  'Fig',
  'Honeydew',
  'Apple',
  'Kiwi',
  'Lemon',
  'Grape',
  'Cherry',
  'Quince',
  'Nectarine',
  'Date',
  'Mango',
  'Orange',
  'Papaya',
];

const MESSAGE_CATEGORIES = [
  'Efficacy',
  'Patient selection',
  'Duration',
  'Convenience',
  'Safety',
  'Survival',
  'Unmet needs',
] as const;

export function MessageEditorModal({
  open,
  onClose,
  message,
  messageGenerationSessionId,
  brands,
  onSave,
  enableSegmentSelection = true,
  preSelectedSegmentationRunId = null,
  preSelectedSegmentId = null,
}: MessageEditorModalProps): React.JSX.Element {
  const effectiveBrands = brands.length > 0 ? brands : DEFAULT_BRANDS;
  const [title, setTitle] = useState(message?.title ?? '');
  const [messageText, setMessageText] = useState(message?.body ?? '');
  const [category, setCategory] = useState(message?.category ?? MESSAGE_CATEGORIES[0] ?? '');
  const [brand, setBrand] = useState(message?.brand ?? 'Elderberry');
  const [status, setStatus] = useState<'draft' | 'approved' | 'published'>(
    (message?.status as 'draft' | 'approved' | 'published') ?? 'draft'
  );
  const [selectedRunId, setSelectedRunId] = useState<number | null>(preSelectedSegmentationRunId);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(preSelectedSegmentId);

  const apiClient = useChatApiClient();
  const queryClient = useQueryClient();

  // Fetch segmentation runs
  const { data: segmentationRuns = [] } = useQuery({
    queryKey: ['segmentation-runs', { status: 'complete' }],
    queryFn: async () => {
      const response = await apiClient.authenticatedClient.get('/segmentation-runs', {
        params: { status: 'complete', take: 100 },
      });
      return response.data.runs || [];
    },
    enabled: open && enableSegmentSelection,
  });

  // Fetch segments for selected run
  const { data: selectedRunData } = useQuery({
    queryKey: ['segmentation-run', selectedRunId],
    queryFn: async () => {
      const response = await apiClient.authenticatedClient.get(`/segmentation-runs/${selectedRunId}`);
      return response.data.run;
    },
    enabled: !!selectedRunId,
  });

  const segments = selectedRunData?.segments || [];

  const { mutate: createMessage, isPending: isCreating } = useMutation({
    mutationFn: (data: {
      messageGenerationSessionId?: number;
      title: string;
      body: string;
      category: string | null;
      brand: string | null;
      status: 'draft' | 'approved' | 'published';
      segmentationSessionId?: string | null;
      segmentId?: string | null;
    }) =>
      apiClient.messageGeneration.createMessage({
        ...(data.messageGenerationSessionId
          ? { messageGenerationSessionId: data.messageGenerationSessionId }
          : {}),
        title: data.title,
        body: data.body,
        category: data.category,
        brand: data.brand,
        status: data.status,
        segmentationSessionId: data.segmentationSessionId,
        segmentId: data.segmentId,
      }),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['generated-messages'] });
      onSave?.(saved);
      onClose();
    },
  });

  const { mutate: updateMessage, isPending: isUpdating } = useMutation({
    mutationFn: (data: {
      messageId: number;
      title: string;
      body: string;
      category: string | null;
      brand: string | null;
      status: 'draft' | 'approved' | 'published';
    }) =>
      apiClient.messageGeneration.updateMessageById(data.messageId, {
        title: data.title,
        body: data.body,
        category: data.category,
        brand: data.brand,
        status: data.status,
      }),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['generated-messages'] });
      onSave?.(saved);
      onClose();
    },
  });

  const isSubmitting = isCreating || isUpdating;

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);

  const handleMessageChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageText(e.target.value);
  }, []);

  const handleCategoryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setCategory(e.target.value);
  }, []);

  const handleBrandChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setBrand(e.target.value);
  }, []);

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(e.target.value as 'draft' | 'approved' | 'published');
  }, []);

  const handleRunChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRunId(e.target.value ? Number(e.target.value) : null);
    setSelectedSegmentId(null); // Clear segment when run changes
  }, []);

  const handleSegmentChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSegmentId(e.target.value || null);
  }, []);

  const handleSubmit = useCallback(() => {
    const payload = {
      ...(messageGenerationSessionId ? { messageGenerationSessionId } : {}),
      title,
      body: messageText,
      category: category || null,
      brand: brand || null,
      status,
      ...(selectedRunId && selectedRunData ? { segmentationSessionId: selectedRunData.sessionId } : {}),
      ...(selectedSegmentId ? { segmentId: selectedSegmentId } : {}),
    };

    if (message) {
      updateMessage({ messageId: message.id, ...payload });
    } else {
      createMessage(payload);
    }
  }, [
    messageGenerationSessionId,
    title,
    messageText,
    category,
    brand,
    status,
    selectedRunId,
    selectedSegmentId,
    selectedRunData,
    message,
    createMessage,
    updateMessage,
  ]);

  const canSubmit = useMemo(() => {
    return title.trim().length > 0 && messageText.trim().length > 0 && messageText.length <= 500;
  }, [title, messageText]);

  const modalTitle = message ? `Edit Message: ${message.title}` : 'Create New Message';

  return (
    <>
      {open && (
        <Global
          styles={css`
            body {
              overflow: hidden !important;
              --z-index-modal: 999999 !important;
            }
          `}
        />
      )}
      <StyledModal
        open={open}
        onClose={onClose}
        disableEscapeClose={isSubmitting}
        disableOverlayClick={isSubmitting}
        classes={{
          root: 'message-editor-modal-root',
          wrap: 'message-editor-modal-wrap',
        }}
      >
        <StyledBody>
          <StyledHeader>{modalTitle}</StyledHeader>

          <FormContainer>
            <FormRow>
              <Label>Message *</Label>
              <StyledInput
                autoFocus
                value={title}
                onChange={handleTitleChange}
                placeholder="Enter message title"
                disabled={isSubmitting}
              />
            </FormRow>

            <FormRow>
              <Label>Description *</Label>
              <MessageInputWrapper>
                <StyledTextarea
                  value={messageText}
                  onChange={handleMessageChange}
                  placeholder="Enter marketing message content..."
                  disabled={isSubmitting}
                  minRows={3}
                />
                <CharCount $overLimit={messageText.length > 500}>
                  {messageText.length} / 500 characters
                </CharCount>
              </MessageInputWrapper>
            </FormRow>

            <FormRow>
              <Label>Category</Label>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value as string)}
                disabled={isSubmitting}
                options={MESSAGE_CATEGORIES.reduce((acc, cat) => ({ ...acc, [cat]: cat }), {})}
              />
            </FormRow>

            <FormRow>
              <Label>Brand</Label>
              <Select
                value={brand}
                onChange={(e) => setBrand(e.target.value as string)}
                disabled={isSubmitting}
                options={effectiveBrands.reduce((acc, b) => ({ ...acc, [b]: b }), {})}
              />
            </FormRow>

            <FormRow>
              <Label>Status</Label>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value as 'draft' | 'approved' | 'published')}
                disabled={isSubmitting}
                options={{
                  draft: 'Draft',
                  approved: 'Approved',
                  published: 'Published',
                }}
              />
            </FormRow>

            {enableSegmentSelection && (
              <>
                <FormRow>
                  <Label>Segmentation Run</Label>
                  <Select
                    value={selectedRunId || ''}
                    onChange={(e) => {
                      setSelectedRunId(e.target.value ? Number(e.target.value) : null);
                      setSelectedSegmentId(null);
                    }}
                    disabled={isSubmitting}
                    placeholder="None"
                    options={segmentationRuns.reduce(
                      (acc: any, run: any) => ({
                        ...acc,
                        [run.id]: `${run.projectTitle || run.name || run.runKey} (${new Date(
                          run.createdOn
                        ).toLocaleDateString()})`,
                      }),
                      {}
                    )}
                  />
                </FormRow>

                {selectedRunId && (
                  <FormRow>
                    <Label>Segment</Label>
                    <Select
                      value={selectedSegmentId || ''}
                      onChange={(e) => setSelectedSegmentId(e.target.value as string)}
                      disabled={isSubmitting || !segments.length}
                      placeholder="None"
                      options={segments.reduce(
                        (acc: any, segment: any) => ({ ...acc, [segment.identifier]: segment.name }),
                        {}
                      )}
                    />
                  </FormRow>
                )}
              </>
            )}

            <InfoText>* Required fields.</InfoText>
          </FormContainer>
        </StyledBody>

        <StyledButtonSet>
          <ButtonOutlined borderWidth="thin" color="silver" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </ButtonOutlined>
          <ButtonActivityIndicator
            color="primary"
            indicatorSize={12}
            disabled={!canSubmit}
            implicitDisable={isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit}
          >
            {message ? 'Save Changes' : 'Create Message'}
          </ButtonActivityIndicator>
        </StyledButtonSet>
      </StyledModal>
    </>
  );
}

// Styled Components - Using CSS variables for z-index
const StyledModal = styled(Modal)`
  &.message-editor-modal-root {
    z-index: 999999 !important;
    width: fit-content !important;
    max-width: 90vw !important;
    height: fit-content !important;
    overflow-y: auto !important;
  }

  .message-editor-modal-wrap {
    padding: 0 !important;
    position: relative;

    > div:first-of-type {
      padding: 4px 8px 0 0;

      > div {
        font-size: 18px;
        line-height: 18px;
      }
    }
  }
`;

const StyledBody = styled(Body)`
  padding: 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledHeader = styled(Header)`
  color: var(--black);
  font-family: var(--font-bold);
  font-size: 22px;
  font-weight: 600;
  text-align: center;
  margin: 0;
  padding: 12px 0 8px 0;
  width: 100%;
`;

const StyledButtonSet = styled(ButtonSet)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 8px 0 12px 0;

  button {
    min-width: 130px;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 40px 10px 40px;
  width: fit-content;
  min-width: 600px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 140px 400px;
  gap: 12px;
  align-items: center;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--black-l);
  font-family: var(--font-bold);
  padding-top: 0;
  text-align: right;
  white-space: nowrap;
`;

const MessageInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const StyledInput = styled(Input)`
  font-family: ${({ theme }) => 'HarmoniaSans W01, Helvetica Neue, Helvetica, Arial, sans-serif'};
  font-size: 14px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => '#e0e0e0'};
  border-radius: 4px;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.palette.sentiment.primary.main || '#28aae1'};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.palette.sentiment.primary.light || 'rgba(40, 170, 225, 0.1)'};
  }

  &:disabled {
    background: ${({ theme }) => '#f5f5f5'};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const StyledTextarea = styled(Textarea)`
  font-family: ${({ theme }) => 'HarmoniaSans W01, Helvetica Neue, Helvetica, Arial, sans-serif'};
  font-size: 14px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => '#e0e0e0'};
  border-radius: 4px;
  resize: vertical;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.palette.sentiment.primary.main || '#28aae1'};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.palette.sentiment.primary.light || 'rgba(40, 170, 225, 0.1)'};
  }

  &:disabled {
    background: ${({ theme }) => '#f5f5f5'};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const CharCount = styled.div<{ $overLimit: boolean }>`
  font-size: 11px;
  color: ${(props) => (props.$overLimit ? '#d32f2f' : '#666')};
  text-align: right;
  font-weight: ${(props) => (props.$overLimit ? 600 : 400)};
`;

const InfoText = styled.p`
  font-size: 12px;
  color: var(--gray-d);
  font-style: italic;
  margin: 4px 0 0 152px;
  padding: 0;
`;
